import LineSearchIcon from 'components/Luxkit/Icons/line/LineSearchIcon'
import BodyText from 'components/Luxkit/Typography/BodyText'
import config from 'constants/config'
import React, { useCallback } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import useGlobalSearchVerticalTabs from 'hooks/GlobalSearch/useGlobalSearchVerticalTabs'
import { plmTabClick, searchNavCTAClick, searchNavCTAHover } from 'analytics/eventDefinitions'
import Clickable from 'components/Common/Clickable'
import { addGTMEvent } from 'api/googleTagManager'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import useCurrentGlobalSearchVerticalPage from 'hooks/GlobalSearch/useCurrentGlobalSearchVerticalPage'
import TextLink from 'components/Luxkit/TextLink'
import { connect } from 'react-redux'
import { getPeopleLikeMeEntryExperimentVariant } from 'selectors/searchSelectors'
import ProductNameFlipper from './ProductFlipper'
import Group from 'components/utils/Group'
import IconButton from 'components/Luxkit/Button/IconButton'
import { useTranslation } from 'react-i18next'

const SearchBar = styled(Clickable)`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  padding: ${rem(8)};
  margin: auto;
  overflow: hidden;
  white-space: nowrap;
  gap: ${rem(4)};
  box-shadow: ${({ theme }) => theme.shadow.flat.small};
`

const SearchContainer = styled.div`
  display: flex;
  border: 1px solid ${({ theme }) => theme.palette.neutral.five};
`

const StyledBodyText = styled(BodyText)`
  margin-left: ${rem(12)};
`

const InspirationBar = styled(Clickable)`
  white-space: nowrap;
  display: flex;
  align-items: center;
  padding-left: ${rem(12)};
  margin: ${rem(12)};
  margin-left: 0;
  border-left: 1px solid ${({ theme }) => theme.palette.neutral.five};
`

interface MappedStateProps {
  shouldShowPeopleLikeMeEntry: boolean
}

function SearchToggle(props: MappedStateProps) {
  const { shouldShowPeopleLikeMeEntry } = props
  const globalSearchVerticalTabs = useGlobalSearchVerticalTabs()
  const { t } = useTranslation('common')

  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()
  const currentSearchVerticalPage = useCurrentGlobalSearchVerticalPage()

  const handleSearchButtonClick = useCallback(() => {
    if (!currentSearchVerticalPage) {
      toggleSearchVertical([...globalSearchVerticalTabs.keys()][0])
    } else {
      toggleSearchVertical(currentSearchVerticalPage)
    }
    addGTMEvent(searchNavCTAClick())
  }, [currentSearchVerticalPage, globalSearchVerticalTabs, toggleSearchVertical])

  const handleSearchButtonMouseEnter = useCallback(function() {
    addGTMEvent(searchNavCTAHover())
  }, [])

  const handleGetInspiredClick = useCallback(() => addGTMEvent(plmTabClick()), [])

  return (
    <SearchContainer>
      <SearchBar
        role="presentation"
        onClick={handleSearchButtonClick}
        onMouseEnter={handleSearchButtonMouseEnter}
      >
        <Group direction="horizontal" gap={4}>
          <StyledBodyText variant="small" colour="neutral-two">
            {t(config.header.searchBarText)}
          </StyledBodyText>
          <ProductNameFlipper />
        </Group>
        <IconButton kind="primary" shape="square" nonInteractable size="small">
          <LineSearchIcon />
        </IconButton>
      </SearchBar>
      {shouldShowPeopleLikeMeEntry && <InspirationBar
        role="presentation"
        to="/people-like-me/inspiration"
      >
        <BodyText variant="small" colour="neutral-four">
          or&nbsp;<TextLink onClick={handleGetInspiredClick}>Get inspired</TextLink>
        </BodyText>
      </InspirationBar>}
    </SearchContainer>
  )
}

export default connect<MappedStateProps, undefined, any, App.State>((appState) => {
  const peopleLikeMeExperimentVariant = getPeopleLikeMeEntryExperimentVariant(appState)
  return {
    shouldShowPeopleLikeMeEntry: config.ENABLE_PEOPLE_LIKE_ME_ENTRY && (peopleLikeMeExperimentVariant === '1' || peopleLikeMeExperimentVariant === '2'),
  }
})(SearchToggle)
